import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Download, Loader2, ShieldAlert, AlertTriangle } from 'lucide-react';
import {
  fetchTenantBilling,
  startTenantBillingSetupIntent,
  createTenantSubscription,
  switchTenantCadence,
  enableTenantDlm,
  disableTenantDlm,
  formatCents,
  type TenantBillingSummary,
} from '@/lib/api';
import formStyles from '@/components/ui/Form.module.css';
import { PLAN_PRICE_UNCONFIRMED, planCost, planPriceLine, type PlanPricePair } from '@/lib/planPrices';

/**
 * Shared tenant subscription billing surface.
 *
 * One component, two homes:
 *   - the operator's own Settings page (scoped to their tenant), and
 *   - the super_admin tenant detail page (scoped to the selected tenant).
 *
 * Both reach the SAME route family, gated server-side by
 * requireTenantScope (own tenant OR super_admin). The layout mirrors the
 * fleet billing tab on FleetDetailPage so the two surfaces read the same.
 *
 * Three states, driven by the billing summary:
 *   1. No Stripe customer  → capture billing email + add a card. The
 *      setup-intent call creates the customer and advances the state.
 *   2. Customer, no sub     → create the per-charger subscription
 *      (cadence + optional promo). A card can be added/updated too.
 *   3. Active subscription  → plan + status + payment method + history.
 *
 * Card capture mirrors the fleet flow: the setup-intent mints a Stripe
 * SetupIntent and we surface its client_secret (full Stripe Elements is
 * a follow-up shared with the fleet tab, which defers it the same way).
 *
 * Past-due and withholding state render as banners at the top. Both are
 * read-only here: the dunning job sets them and the tenant webhook clears
 * withholding on payment. There is no manual release control in this view.
 */

// No price lives here (Warren, 21 October 2026, night: "Read both prices
// from the live Stripe Price objects, never calculate one from the
// other"). The summary carries each cadence's per-charger price from its
// own Stripe Price, null where Stripe has none; the authoritative charge
// is still the Stripe upcoming-invoice estimate the summary carries.
const NO_PRICES: PlanPricePair = { annual: null, monthly: null };

function fmtDate(value: string | number | null | undefined): string {
  if (!value) return '—';
  const ts = typeof value === 'number' ? value * 1000 : new Date(value).getTime();
  if (!Number.isFinite(ts)) return '—';
  return new Date(ts).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function statusColor(status: string | null): string {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'var(--evi-accent, #2ECC71)';
    case 'past_due':
    case 'unpaid':
      return 'var(--evi-warning, #f59e0b)';
    case 'canceled':
    case 'incomplete':
      return 'var(--evi-error, #ef4444)';
    default:
      return 'var(--color-tx-m)';
  }
}

const cardStyle: React.CSSProperties = {
  padding: '16px 18px', background: 'var(--color-sf2, #1a1a1a)',
  border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 10,
};
const cardLabelStyle: React.CSSProperties = {
  margin: 0, fontSize: 10, textTransform: 'uppercase',
  color: 'var(--color-tx-m)', letterSpacing: '1.2px',
};
const accentBtnStyle: React.CSSProperties = {
  padding: '8px 14px', fontSize: 13,
  background: 'var(--evi-accent, #2ECC71)', color: '#000',
  border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer',
};
const outlineBtnStyle: React.CSSProperties = {
  padding: '8px 14px', fontSize: 13,
  background: 'transparent', color: 'var(--evi-accent, #2ECC71)',
  border: '1px solid var(--evi-accent, #2ECC71)', borderRadius: 8,
  fontWeight: 600, cursor: 'pointer',
};

export function TenantBilling({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const billingQ = useQuery({
    queryKey: ['tenant-billing', tenantId],
    queryFn: () => fetchTenantBilling(tenantId),
  });

  const [emailInput, setEmailInput] = useState('');
  const [promoInput, setPromoInput] = useState('');
  const [cadenceChoice, setCadenceChoice] = useState<'monthly' | 'annual'>('monthly');
  const [setupInfo, setSetupInfo] = useState<{ client_secret: string } | null>(null);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: ['tenant-billing', tenantId] });

  const setupIntentM = useMutation({
    mutationFn: (email: string) => startTenantBillingSetupIntent(tenantId, email),
    onSuccess: (data) => {
      setSetupInfo({ client_secret: data.client_secret });
      setActionError(null);
      invalidate();
    },
    onError: (e: Error) => setActionError(e.message),
  });

  const createSubM = useMutation({
    mutationFn: () =>
      createTenantSubscription(tenantId, {
        billing_cadence: cadenceChoice,
        ...(promoInput.trim() ? { promo_code: promoInput.trim() } : {}),
      }),
    onSuccess: () => {
      setActionError(null);
      setPromoInput('');
      invalidate();
    },
    onError: (e: Error) => setActionError(e.message),
  });

  const switchM = useMutation({
    mutationFn: (cadence: 'monthly' | 'annual') => switchTenantCadence(tenantId, cadence),
    onSuccess: () => {
      setSwitchOpen(false);
      setActionError(null);
      invalidate();
    },
    onError: (e: Error) => setActionError(e.message),
  });

  // DLM add-on enable/disable (unit 5). On success the summary refetches so
  // the state, managed count, and cost re-render.
  const dlmM = useMutation({
    mutationFn: (action: 'enable' | 'disable') =>
      action === 'enable' ? enableTenantDlm(tenantId) : disableTenantDlm(tenantId),
    onSuccess: () => { setActionError(null); invalidate(); },
    onError: (e: Error) => setActionError(e.message),
  });

  if (billingQ.isLoading) {
    return (
      <div style={{ padding: 16, color: 'var(--color-tx-m)' }}>
        <Loader2 size={14} className="spin" /> Loading billing…
      </div>
    );
  }
  if (billingQ.isError || !billingQ.data) {
    return (
      <p style={{ color: 'var(--evi-error, #ef4444)' }}>
        <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
        Could not load billing.
      </p>
    );
  }

  const b: TenantBillingSummary = billingQ.data;
  const hasCustomer = Boolean(b.stripe_customer_id);
  const hasSub = Boolean(b.stripe_subscription_id);
  const isAnnual = b.billing_cadence === 'annual';
  const prices = b.plan_prices?.per_charger ?? NO_PRICES;
  const perCharger = prices[b.billing_cadence];
  const dlmPrice = (b.plan_prices?.load_management ?? NO_PRICES)[b.billing_cadence];
  const cadenceLabel = isAnnual ? '/year' : '/month';
  // The Plan card multiplies what Stripe bills — the sync's
  // max(billable live count, checkout seat floor) — not the roster
  // count, which reads "0 chargers" against an invoice for N while a
  // website subscriber's chargers are still being registered.
  const billedQuantity = b.billed_quantity;
  const floorAboveLive = (b.subscribed_charger_floor ?? 0) > b.billable_charger_count;
  const totalCents = planCost([{ price: perCharger, quantity: billedQuantity }]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {actionError && (
        <div style={{
          padding: '10px 12px',
          background: 'color-mix(in srgb, var(--evi-error, #ef4444) 12%, transparent)',
          border: '1px solid color-mix(in srgb, var(--evi-error, #ef4444) 35%, transparent)',
          borderRadius: 8, color: 'var(--evi-error, #ef4444)', fontSize: 13,
        }}>
          {actionError}
        </div>
      )}

      {/* Withholding indicator — read-only. Released by the tenant
          webhook on subscription payment, not from this surface. */}
      {b.withholding_active && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          padding: '10px 12px',
          background: 'color-mix(in srgb, var(--evi-warning, #f59e0b) 12%, transparent)',
          border: '1px solid color-mix(in srgb, var(--evi-warning, #f59e0b) 35%, transparent)',
          borderRadius: 8, color: 'var(--evi-warning, #f59e0b)', fontSize: 13,
        }}>
          <ShieldAlert size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <strong>Payouts withheld.</strong> While this subscription is overdue, EVI holds
            the operator&rsquo;s session payouts on the platform and keeps its own fee. The held
            amount releases automatically once the subscription invoice is paid.
          </span>
        </div>
      )}

      {/* Past-due banner. subscription_status drives this independently of
          withholding, since day 0-13 are past_due without a hold yet. */}
      {b.subscription_status === 'past_due' && (
        <div style={{
          padding: '10px 12px',
          background: 'color-mix(in srgb, var(--evi-warning, #f59e0b) 10%, transparent)',
          border: '1px solid color-mix(in srgb, var(--evi-warning, #f59e0b) 30%, transparent)',
          borderRadius: 8, color: 'var(--evi-warning, #f59e0b)', fontSize: 13,
        }}>
          <strong>Subscription past due{b.past_due_since ? ` since ${fmtDate(b.past_due_since)}` : ''}.</strong>{' '}
          Update the card below or pay the latest invoice to bring it current.
        </div>
      )}

      {!hasSub ? (
        // ── Onboarding / create flow ───────────────────────────────
        !hasCustomer ? (
          <section style={cardStyle}>
            <p style={cardLabelStyle}>Get started</p>
            <p style={{ margin: '8px 0 12px', fontSize: 13, color: 'var(--color-tx-m)', lineHeight: 1.5 }}>
              Add a billing contact and a card to start the per-charger subscription.
              {' '}Monthly: <strong>{planPriceLine(prices.monthly, formatCents)}</strong>.
              {' '}Yearly: <strong>{planPriceLine(prices.annual, formatCents)}</strong>.
            </p>
            <label className={formStyles.label}>Billing email</label>
            <input
              className={formStyles.input}
              type="email"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="billing@building.com"
            />
            <button
              type="button"
              onClick={() => {
                setActionError(null);
                if (!emailInput.trim()) {
                  setActionError('A billing email is required.');
                  return;
                }
                setupIntentM.mutate(emailInput.trim());
              }}
              disabled={setupIntentM.isPending}
              style={{ ...accentBtnStyle, marginTop: 12 }}
            >
              {setupIntentM.isPending ? 'Starting…' : 'Add card'}
            </button>
          </section>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 12 }}>
            {/* Create subscription */}
            <section style={cardStyle}>
              <p style={cardLabelStyle}>Start subscription</p>
              <p style={{ margin: '8px 0 12px', fontSize: 13, color: 'var(--color-tx-m)', lineHeight: 1.5 }}>
                {b.charger_count} active charger{b.charger_count === 1 ? '' : 's'},{' '}
                {planPriceLine(prices[cadenceChoice], formatCents)}. The quantity tracks your
                charger count automatically.
              </p>
              <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                {(['monthly', 'annual'] as const).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCadenceChoice(c)}
                    style={{
                      padding: '6px 14px', fontSize: 12, borderRadius: 999,
                      background: cadenceChoice === c ? 'var(--evi-accent, #2ECC71)' : 'transparent',
                      color: cadenceChoice === c ? '#000' : 'var(--color-tx)',
                      border: '1px solid var(--evi-accent, #2ECC71)',
                      fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize',
                    }}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <label className={formStyles.label}>Promo code (optional)</label>
              <input
                className={formStyles.input}
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value)}
                placeholder="SWITCH2EVI"
              />
              <p style={{ fontSize: 11, color: 'var(--color-tx-m)', margin: '6px 0 0', lineHeight: 1.5 }}>
                A Stripe promotion code. Validated when the subscription is created; an invalid or
                expired code is rejected.
              </p>
              <button
                type="button"
                onClick={() => { setActionError(null); createSubM.mutate(); }}
                disabled={createSubM.isPending}
                style={{ ...accentBtnStyle, marginTop: 12 }}
              >
                {createSubM.isPending ? 'Creating…' : `Start ${cadenceChoice} subscription`}
              </button>
            </section>

            {/* Payment method (customer exists, no sub yet) */}
            <PaymentMethodCard
              card={b.default_card}
              busy={setupIntentM.isPending}
              onUpdate={() => {
                setActionError(null);
                setupIntentM.mutate(b.billing_email ?? emailInput.trim());
              }}
            />
          </div>
        )
      ) : (
        // ── Active subscription management ──────────────────────────
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 12 }}>
          {/* Plan card */}
          <section style={cardStyle}>
            <p style={cardLabelStyle}>Plan · {isAnnual ? 'Annual' : 'Monthly'}</p>
            {totalCents !== null ? (
              <p style={{ margin: '8px 0 0', fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {formatCents(totalCents)}
                <span style={{ fontSize: 14, color: 'var(--color-tx-m)', fontWeight: 400 }}>{cadenceLabel}</span>
              </p>
            ) : (
              <p style={{ margin: '8px 0 0', fontSize: 14, fontWeight: 600 }}>{PLAN_PRICE_UNCONFIRMED}.</p>
            )}
            <ul style={{ margin: '10px 0 12px', padding: 0, listStyle: 'none', fontSize: 12, color: 'var(--color-tx-m)' }}>
              <li style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                <span>{billedQuantity} charger{billedQuantity === 1 ? '' : 's'}{perCharger ? <> × {formatCents(perCharger.unit_price_cents, perCharger.currency)}{cadenceLabel}</> : `: ${PLAN_PRICE_UNCONFIRMED}`}</span>
                {totalCents !== null && <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCents(totalCents)}{cadenceLabel}</span>}
              </li>
              {floorAboveLive && (
                <li style={{ padding: '2px 0 0', fontSize: 11 }}>
                  {b.subscribed_charger_floor} seats reserved · {b.billable_charger_count} registered
                </li>
              )}
            </ul>
            <button
              type="button"
              onClick={() => setSwitchOpen(true)}
              style={accentBtnStyle}
            >
              Switch to {isAnnual ? 'monthly' : 'annual'}
            </button>
          </section>

          {/* Status card */}
          <section style={cardStyle}>
            <p style={cardLabelStyle}>Status</p>
            <p style={{ margin: '8px 0 0', fontSize: 16, fontWeight: 700, color: statusColor(b.subscription_status) }}>
              {b.subscription_status ?? 'No subscription'}
            </p>
            {b.subscription_status === 'trialing' && b.trial_ends_at && (
              <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--color-tx-m)' }}>
                Trial ends {fmtDate(b.trial_ends_at)}
              </p>
            )}
            {b.current_period_end && (
              <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--color-tx-m)' }}>
                Next billing: {fmtDate(b.current_period_end)}
              </p>
            )}
            {b.upcoming_invoice && (
              <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--color-tx-m)' }}>
                Next charge estimate: <strong>{formatCents(b.upcoming_invoice.amount_due_cents)}</strong>
              </p>
            )}
          </section>

          {/* Payment method card */}
          <PaymentMethodCard
            card={b.default_card}
            busy={setupIntentM.isPending}
            onUpdate={() => {
              setActionError(null);
              setupIntentM.mutate(b.billing_email ?? '');
            }}
          />

          {/* DLM add-on control. Gated on an active subscription so enabling
              always attaches a billing item (no free DLM via the UI). */}
          <section style={cardStyle}>
            <p style={cardLabelStyle}>Load management</p>
            <p style={{ margin: '8px 0 0', fontSize: 16, fontWeight: 700, color: b.dlm_enabled ? 'var(--evi-accent, #2ECC71)' : 'var(--color-tx-m)' }}>
              {b.dlm_enabled ? 'On' : 'Off'}
            </p>
            {b.dlm_enabled ? (
              <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--color-tx-m)' }}>
                {b.dlm_managed_charger_count} managed charger{b.dlm_managed_charger_count === 1 ? '' : 's'}
                {b.dlm_cost_estimate_cents !== null && (
                  <> · <strong>{formatCents(b.dlm_cost_estimate_cents)}</strong>{cadenceLabel}</>
                )}
              </p>
            ) : (
              <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--color-tx-m)', lineHeight: 1.5 }}>
                Caps each circuit's chargers to its breaker budget. Price:{' '}
                {planPriceLine(dlmPrice, formatCents, 'per managed charger')}
                {b.dlm_cost_estimate_cents !== null && b.dlm_managed_charger_count > 0
                  ? <>, about <strong>{formatCents(b.dlm_cost_estimate_cents)}</strong>{cadenceLabel} for {b.dlm_managed_charger_count} now.</>
                  : '.'}
              </p>
            )}
            <button
              type="button"
              disabled={dlmM.isPending}
              onClick={() => {
                setActionError(null);
                if (b.dlm_enabled) {
                  if (confirm('Disable load management for this building? This removes the per-circuit caps, so an oversubscribed site can exceed its breaker and charging may be interrupted.')) {
                    dlmM.mutate('disable');
                  }
                } else {
                  const cost = b.dlm_cost_estimate_cents !== null && b.dlm_managed_charger_count > 0
                    ? ` Billed per managed charger, about ${formatCents(b.dlm_cost_estimate_cents)}${cadenceLabel} for ${b.dlm_managed_charger_count} managed charger${b.dlm_managed_charger_count === 1 ? '' : 's'} now.`
                    : ` Price: ${planPriceLine(dlmPrice, formatCents, 'per managed charger')}.`;
                  if (confirm(`Enable load management for this building?${cost}`)) {
                    dlmM.mutate('enable');
                  }
                }
              }}
              style={b.dlm_enabled
                ? { ...outlineBtnStyle, marginTop: 12, color: 'var(--evi-error, #ef4444)', border: '1px solid var(--evi-error, #ef4444)' }
                : { ...accentBtnStyle, marginTop: 12 }}
            >
              {dlmM.isPending ? 'Working…' : (b.dlm_enabled ? 'Disable DLM' : 'Enable DLM')}
            </button>
          </section>
        </div>
      )}

      {/* Invoice history */}
      {hasCustomer && (
        <section>
          <h3 style={{ margin: '8px 0', fontSize: 14 }}>Billing history</h3>
          {b.recent_invoices.length === 0 ? (
            <p style={{ color: 'var(--color-tx-m)' }}>No invoices yet.</p>
          ) : (
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))' }}>
                  <th style={{ textAlign: 'left', padding: 8 }}>Date</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Number</th>
                  <th style={{ textAlign: 'left', padding: 8 }}>Status</th>
                  <th style={{ textAlign: 'right', padding: 8 }}>Amount</th>
                  <th style={{ textAlign: 'right', padding: 8 }}></th>
                </tr>
              </thead>
              <tbody>
                {b.recent_invoices.map((inv) => (
                  <tr key={inv.id} style={{ borderBottom: '1px solid var(--color-brd, var(--evi-border))' }}>
                    <td style={{ padding: 8 }}>{fmtDate(inv.created ?? inv.period_start)}</td>
                    <td style={{ padding: 8 }}>{inv.number ?? inv.id.slice(-8)}</td>
                    <td style={{ padding: 8, color: statusColor(inv.status) }}>{inv.status ?? '—'}</td>
                    <td style={{ padding: 8, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {formatCents(inv.amount_paid_cents || inv.amount_due_cents)}
                    </td>
                    <td style={{ padding: 8, textAlign: 'right' }}>
                      {inv.hosted_invoice_url && (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" style={{ marginRight: 8 }}>
                          View
                        </a>
                      )}
                      {inv.invoice_pdf && (
                        <a href={inv.invoice_pdf} target="_blank" rel="noreferrer">
                          <Download size={12} style={{ verticalAlign: 'middle' }} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}

      {b.stripe_customer_id && (
        <p style={{ marginTop: 4, fontSize: 11, color: 'var(--color-tx-m)' }}>
          Stripe customer: <code>{b.stripe_customer_id}</code>
        </p>
      )}

      {/* Modals */}
      {switchOpen && (
        <SwitchCadenceModal
          fromCadence={b.billing_cadence}
          chargerCount={billedQuantity}
          prices={prices}
          submitting={switchM.isPending}
          onClose={() => setSwitchOpen(false)}
          onConfirm={() => switchM.mutate(isAnnual ? 'monthly' : 'annual')}
        />
      )}
      {setupInfo && (
        <SetupIntentInfoModal
          clientSecret={setupInfo.client_secret}
          onClose={() => setSetupInfo(null)}
        />
      )}
    </div>
  );
}

function PaymentMethodCard({
  card, busy, onUpdate,
}: {
  card: TenantBillingSummary['default_card'];
  busy: boolean;
  onUpdate: () => void;
}) {
  return (
    <section style={cardStyle}>
      <p style={cardLabelStyle}>Payment method</p>
      {card ? (
        <p style={{ margin: '8px 0 0', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
          <CreditCard size={14} /> {card.brand?.toUpperCase()} ···· {card.last4}
          <span style={{ color: 'var(--color-tx-m)', fontSize: 12, marginLeft: 4 }}>
            exp {card.exp_month}/{String(card.exp_year).slice(-2)}
          </span>
        </p>
      ) : (
        <p style={{ margin: '8px 0 0', color: 'var(--color-tx-m)', fontSize: 13 }}>No card on file</p>
      )}
      <button
        type="button"
        onClick={onUpdate}
        disabled={busy}
        style={{ ...outlineBtnStyle, marginTop: 12 }}
      >
        {busy ? 'Starting…' : card ? 'Update payment method' : 'Add card'}
      </button>
    </section>
  );
}

function ModalShell({
  title, children, onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(520px, 92vw)', maxHeight: '90vh', overflowY: 'auto',
          background: 'var(--color-sf, #1a1a1a)',
          border: '1px solid var(--color-brd, var(--evi-border))',
          borderRadius: 12, padding: 20,
        }}
      >
        <h2 style={{ margin: '0 0 14px', fontSize: 16 }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

/** Each cadence's cost from its own Stripe Price, never one worked out from the other and no "you save" derived from
 *  them (Warren, 21 October 2026, night). */
function SwitchCadenceModal({
  fromCadence, chargerCount, prices, submitting, onClose, onConfirm,
}: {
  fromCadence: 'monthly' | 'annual';
  chargerCount: number;
  prices: PlanPricePair;
  submitting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const toAnnual = fromCadence === 'monthly';
  const monthlyTotal = planCost([{ price: prices.monthly, quantity: chargerCount }]);
  const annualTotal = planCost([{ price: prices.annual, quantity: chargerCount }]);

  return (
    <ModalShell title={toAnnual ? 'Switch to annual billing' : 'Switch to monthly billing'} onClose={onClose}>
      <p style={{ fontSize: 13, color: 'var(--color-tx-m)' }}>
        {toAnnual
          ? 'Switch to annual billing, paid for the year ahead. The change takes effect on the next billing cycle, and Stripe charges the prorated difference on the next invoice.'
          : 'Switch back to monthly. The change takes effect on the next billing cycle, and Stripe refunds the unused annual portion as a prorated credit.'}
      </p>
      <div style={{
        margin: '12px 0', padding: 14,
        background: 'var(--color-sf2, #1a1a1a)',
        border: '1px solid var(--color-brd, var(--evi-border))',
        borderRadius: 8, fontSize: 13,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span>Monthly cost</span>
          <strong>{monthlyTotal !== null ? `${formatCents(monthlyTotal)}/month` : PLAN_PRICE_UNCONFIRMED}</strong>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span>Annual cost</span>
          <strong>{annualTotal !== null ? `${formatCents(annualTotal)}/year` : PLAN_PRICE_UNCONFIRMED}</strong>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose}
          style={{ padding: '8px 14px', background: 'transparent', border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 8, cursor: 'pointer', color: 'var(--color-tx)' }}>
          Cancel
        </button>
        <button type="button" onClick={onConfirm} disabled={submitting} style={accentBtnStyle}>
          {submitting ? 'Switching…' : `Switch to ${toAnnual ? 'annual' : 'monthly'}`}
        </button>
      </div>
    </ModalShell>
  );
}

function SetupIntentInfoModal({
  clientSecret, onClose,
}: { clientSecret: string; onClose: () => void }) {
  return (
    <ModalShell title="Add or update card" onClose={onClose}>
      <p style={{ fontSize: 13, color: 'var(--color-tx-m)' }}>
        A Stripe SetupIntent has been created. Hand the client_secret to a Stripe Elements
        integration to collect the card client-side, or use the Stripe Dashboard to attach a card
        to this operator&rsquo;s customer.
      </p>
      <p style={{
        fontSize: 11, color: 'var(--color-tx-m)', wordBreak: 'break-all', margin: '8px 0',
        padding: 10, background: 'var(--color-sf2, #1a1a1a)',
        border: '1px solid var(--color-brd, var(--evi-border))', borderRadius: 6,
      }}>
        <code>{clientSecret}</code>
      </p>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose} style={accentBtnStyle}>Close</button>
      </div>
    </ModalShell>
  );
}
